import { TourTypeSelect, parseTourType, type TourType } from '@/components/travel/direction';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  Filter,
  Package,
  MoreHorizontal,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Star,
  Image,
  Pencil,
  Trash2,
  Eye,
  Archive,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { PackageModal } from '@/components/travel/packages/package-modal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-red-100 text-red-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'status.draft',
  PUBLISHED: 'status.published',
  ARCHIVED: 'status.archived',
};

const difficultyLabelKeys: Record<string, string> = {
  EASY: 'difficulty.easy',
  MODERATE: 'difficulty.moderate',
  CHALLENGING: 'difficulty.challenging',
  DIFFICULT: 'difficulty.difficult',
};

const difficultyColors: Record<string, string> = {
  EASY: 'bg-green-100 text-green-700',
  MODERATE: 'bg-blue-100 text-blue-700',
  CHALLENGING: 'bg-orange-100 text-orange-700',
  DIFFICULT: 'bg-red-100 text-red-700',
};

type PackageStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
type PackageDifficulty = 'EASY' | 'MODERATE' | 'CHALLENGING' | 'DIFFICULT';

type PackageFormData = {
  tourType?: TourType | null;
  id: string;
  code: string;
  name: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  duration: number;
  destinations: string[];
  categoryId?: string | null;
  tags: string[];
  difficulty: PackageDifficulty;
  status: PackageStatus;
  isFeatured: boolean;
};

export function PackagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tourType = searchParams.get('tourType') === 'UNASSIGNED' ? 'UNASSIGNED' : parseTourType(searchParams.get('tourType'));
  const { t } = useTranslation('packages');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('PACKAGES', 'CREATE');
  const canEdit = usePermission('PACKAGES', 'EDIT');
  const canDelete = usePermission('PACKAGES', 'DELETE');
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'travel:packages:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<PackageStatus | undefined>(
    'travel:packages:statusFilter',
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageFormData | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<string | null>(null);
  const limit = 9;
  useEffect(() => { setPage(1); }, [tourType]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.package.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter,
    tourType,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.package.delete.useMutation({
    onSuccess: () => {
      utils.package.list.invalidate();
      showSuccessToast(t('packageDeleted'));
      setDeleteConfirmOpen(false);
      setPackageToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message || t('packageDeleteError'));
    },
  });

  const updateStatusMutation = trpc.package.updateStatus.useMutation({
    onSuccess: () => {
      utils.package.list.invalidate();
      showSuccessToast(t('statusUpdated'));
    },
    onError: (error) => {
      showErrorToast(error.message || t('statusUpdateError'));
    },
  });

  const handleAddPackage = () => {
    setEditingPackage(undefined);
    setIsModalOpen(true);
  };

  const handleEditPackage = (pkg: PackageFormData) => {
    setEditingPackage(pkg);
    setIsModalOpen(true);
  };

  const handleDeletePackage = (id: string) => {
    setPackageToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (packageToDelete) {
      deleteMutation.mutate(packageToDelete);
    }
  };

  const handlePublishPackage = (id: string) => {
    updateStatusMutation.mutate({ id, status: 'PUBLISHED' });
  };

  const handleArchivePackage = (id: string) => {
    updateStatusMutation.mutate({ id, status: 'ARCHIVED' });
  };

  const packages = data?.packages ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 9, total: 0, totalPages: 0 };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleAddPackage}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createPackage')}
          </Button>
        )}
      </div>

      {/* Package Modal */}
      {isModalOpen && (
        <PackageModal defaultTourType={parseTourType(tourType ?? null)} open={isModalOpen} onOpenChange={setIsModalOpen} package={editingPackage} />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('deletePackage')}
        description={t('deleteConfirmation')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <TourTypeSelect filter value={tourType} onChange={value => setSearchParams(previous => { const next = new URLSearchParams(previous); if(value) next.set("tourType",value); else next.delete("tourType"); return next; })} />
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as PackageStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="PUBLISHED">{t('status.published')}</option>
          <option value="DRAFT">{t('status.draft')}</option>
          <option value="ARCHIVED">{t('status.archived')}</option>
        </select>
        <Button variant="outline">
          <Filter className="mr-2 h-4 w-4" />
          {t('moreFilters')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loadingPackages')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('errorLoading')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && packages.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Package className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('noPackagesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {debouncedSearch || statusFilter ? t('adjustFilters') : t('getStarted')}
          </p>
          {!debouncedSearch && !statusFilter && canCreate && (
            <Button className="mt-4" onClick={handleAddPackage}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createPackage')}
            </Button>
          )}
        </div>
      )}

      {/* Packages Grid */}
      {!isLoading && !error && packages.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => (
              <Link
                key={pkg.id}
                to={`/travel/packages/${pkg.id}`}
                className="bg-card group overflow-hidden border transition-shadow hover:shadow-md"
              >
                {/* Package Image */}
                <div className="bg-muted relative h-40">
                  {pkg.media && pkg.media.length > 0 && pkg.media[0]?.url ? (
                    <img
                      src={pkg.media[0].url}
                      alt={pkg.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Image className="text-muted-foreground/30 h-12 w-12" />
                    </div>
                  )}
                  {pkg.isFeatured && (
                    <div className="absolute left-2 top-2 flex items-center gap-1 bg-yellow-500 px-2 py-0.5 text-xs font-medium text-white">
                      <Star className="h-3 w-3" />
                      {t('featured')}
                    </div>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 bg-white/80 hover:bg-white"
                        onClick={(e) => e.preventDefault()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/travel/packages/${pkg.id}`}>
                          <Eye className="mr-2 h-4 w-4" />
                          {t('viewDetails')}
                        </Link>
                      </DropdownMenuItem>
                      {canEdit && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditPackage({
                              id: pkg.id,
                              code: pkg.code,
                              name: pkg.name,
                              slug: pkg.slug,
                              shortDescription: pkg.shortDescription,
                              description: pkg.description,
                              duration: pkg.duration,
                              destinations: pkg.destinations,
                              categoryId: pkg.category?.id,
                              tags: pkg.tags,
                              difficulty: pkg.difficulty,
                              status: pkg.status,
                              isFeatured: pkg.isFeatured,
                            });
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          {tCommon('edit')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {pkg.status !== 'PUBLISHED' && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            handlePublishPackage(pkg.id);
                          }}
                        >
                          <Globe className="mr-2 h-4 w-4" />
                          {t('publish')}
                        </DropdownMenuItem>
                      )}
                      {pkg.status !== 'ARCHIVED' && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            handleArchivePackage(pkg.id);
                          }}
                        >
                          <Archive className="mr-2 h-4 w-4" />
                          {t('archive')}
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeletePackage(pkg.id);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {tCommon('delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Package Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-muted-foreground text-xs">{pkg.code}</p>
                      <h3 className="group-hover:text-primary font-semibold">{pkg.name}</h3>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[pkg.status]}`}>
                      {t(statusLabelKeys[pkg.status] || 'status.unknown')}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${difficultyColors[pkg.difficulty]}`}
                    >
                      {t(difficultyLabelKeys[pkg.difficulty] || 'difficulty.unknown')}
                    </span>
                  </div>

                  <div className="text-muted-foreground mt-4 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      <span>
                        {pkg.duration} {t('days')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      <span className="truncate">{pkg.destinations.slice(0, 2).join(', ')}</span>
                    </div>
                  </div>

                  {pkg.pricing && pkg.pricing.length > 0 && pkg.pricing[0] && (
                    <div className="mt-4 border-t pt-4">
                      <div className="flex items-baseline justify-between">
                        <span className="text-muted-foreground text-sm">{t('startingFrom')}</span>
                        <span className="text-primary text-lg font-bold">
                          {formatCurrency(Number(pkg.pricing[0].price2Pax))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {t('showingResults', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  {tCommon('next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
